import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Save, AlertCircle } from 'lucide-react';
import { getPaymentSettings, updatePaymentSettings } from '../../api/paymentSettingsAdmin';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

export function PaymentSettingsAdminPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getPaymentSettings()
      .then(d => {
        setSettings(d.settings);
        setForm({
          refund_window_days: d.settings.refund_window_days,
          vat_rate: parseFloat(d.settings.vat_rate),
          default_currency: d.settings.default_currency,
        });
      })
      .catch(err => setError(err.response?.data?.error || 'Yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      const updated = await updatePaymentSettings({
        refund_window_days: parseInt(form.refund_window_days, 10),
      });
      setSettings(updated.settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate('/ops-center-f9x2')}
            className="text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={20} />
          </button>
          <Settings size={22} className="text-purple-600" />
          <h1 className="text-xl font-bold text-slate-800">Ödeme Ayarları</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6">
        {loading && <p className="text-slate-500">Yükleniyor...</p>}

        {!loading && settings && (
          <form onSubmit={handleSave} className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Genel</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Aktif Provider</label>
                  <Input value={settings.active_provider} disabled className="font-mono" />
                  <p className="text-xs text-slate-500 mt-1">Şu an sadece 'paytr' destekli</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Para Birimi</label>
                  <Input value={settings.default_currency} disabled />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">KDV Oranı</label>
                <Input
                  value={`%${(parseFloat(settings.vat_rate) * 100).toFixed(0)}`}
                  disabled
                  className="w-32"
                />
                <p className="text-xs text-slate-500 mt-1">
                  TR için %20 (sabit, mevzuat değişimi için backend güncellemesi gerekir)
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">İade</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">İade Süresi (gün)</label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={form.refund_window_days}
                    onChange={(e) => setForm(f => ({ ...f, refund_window_days: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Mesafeli satış mevzuatı için min 14 gün önerilir
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3">
                ✓ Ayarlar kaydedildi
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" loading={saving}>
                <Save size={16} /> Kaydet
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
